import { useCachedTags, type ModelsWithCachedTags } from "@tellescope/tanstack-query";
import { Circle, Plus } from "lucide-react";
import { Badge, Button, Input } from "@tellescope/ui";
import { useForm } from "@tellescope/forms";
import { useState } from "react";
import { useTranslation } from "react-i18next";

export interface AddTagContentProps {
  currentTags: string[];
  model: ModelsWithCachedTags;
  onTagClick: (tagName: string) => Promise<void> | void;
}

export const AddTagContent = ({ currentTags, model, onTagClick }: AddTagContentProps) => {
  const { t } = useTranslation("tags");
  const { data: suggestedTags } = useCachedTags({ model });
  const [isAddingTag, setIsAddingTag] = useState(false);
  const form = useForm({
    fields: {
      newTagName: {
        type: "text",
        initialValue: "",
      },
    },
    onSubmit: async (fields) => {
      setIsAddingTag(false);
      await onTagClick(fields.newTagName);
    },
    resetOnSubmit: true,
  });

  return (
    <div className="p-2">
      {suggestedTags.length ? (
        <>
          <div className="text-xs font-medium text-muted-foreground mb-2">{t("Existing Tags")}</div>
          <div className="space-y-1 max-h-32 overflow-y-auto">
            {suggestedTags.map((tag, i) => (
              <div
                key={`${i}-${tag}`}
                className="flex items-center justify-between p-1.5 rounded hover:bg-muted cursor-pointer"
                onClick={() => onTagClick(tag)}
              >
                <div className="flex items-center gap-2">
                  <Circle
                    className="h-3 w-3"
                    style={{
                      color: "var(--bg-gray-900)",
                      fill: currentTags.includes(tag) ? "var(--bg-gray-900)" : "",
                    }}
                  />
                  <span className="text-sm">{tag}</span>
                </div>
                {currentTags.some((appliedTag) => appliedTag === tag) && (
                  <Badge variant="secondary" className="h-4 px-1.5 text-xs">
                    {t("Applied")}
                  </Badge>
                )}
              </div>
            ))}
          </div>
        </>
      ) : null}
      <div className={suggestedTags.length ? "border-t pt-2 mt-2" : ""}>
        {isAddingTag ? (
          <form {...form.getFormProps()} className="space-y-1">
            <label className="text-sm font-medium">{t("Tag Name")}</label>
            <Input
              {...form.getFieldProps("newTagName")}
              placeholder={t("Enter tag name")}
              className="w-full"
              autoFocus
            />
          </form>
        ) : (
          <Button variant="ghost" size="sm" className="w-full justify-start" onClick={() => setIsAddingTag(true)}>
            <Plus className="mr-2 h-3 w-3" />
            {t("Create New Tag")}
          </Button>
        )}
      </div>
    </div>
  );
};
